/**
 * Competition Zone Widget
 * Dashboard widget showing active competitions and leaderboards
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { useAuth } from '../../contexts/AuthContext';
import {
  Trophy, ChevronRight, Users, Target, Crown, Medal, Zap
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Category configurations  
const CATEGORY_CONFIG = {
  SALES: { label: 'Sales', className: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' },
  OPS: { label: 'Ops', className: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' },
  PRODUCTION: { label: 'Production', className: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400' },
  CREWS: { label: 'Crews', className: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' },
  MARKETING: { label: 'Marketing', className: 'bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-400' },
  LEADERSHIP: { label: 'Leadership', className: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400' },
  GENERAL: { label: 'General', className: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300' }
};

const formatMetricValue = (value, metricType) => {
  switch (metricType) {
    case 'CURRENCY': return `$${value?.toLocaleString() || 0}`;
    case 'PERCENT': return `${value || 0}%`;
    case 'HOURS': return `${value || 0}h`;
    case 'POINTS': return `${value || 0} pts`;
    default: return value?.toLocaleString() || 0;
  }
};

// Mini Competition Card
const MiniCompetitionCard = ({ competition, onClick }) => {
  const categoryConfig = CATEGORY_CONFIG[competition.category] || CATEGORY_CONFIG.GENERAL;
  
  return (
    <div 
      className="p-3 rounded-lg border hover:bg-accent/50 cursor-pointer transition-colors"
      onClick={onClick}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Badge className={`${categoryConfig.className} text-xs`}>{categoryConfig.label}</Badge>
          {competition.my_rank && (
            <Badge variant="outline" className="text-xs">
              <Medal className="h-3 w-3 mr-1" />
              #{competition.my_rank}
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-1 text-xs text-muted-foreground">
          <Users className="h-3 w-3" />
          {competition.participant_count}
        </div>
      </div>
      <p className="font-medium text-sm truncate">{competition.title}</p>
      {competition.my_score !== undefined && (
        <div className="mt-2">
          <div className="flex justify-between text-xs mb-1">
            <span className="text-muted-foreground">Progress</span>
            <span>{formatMetricValue(competition.my_score, competition.metric_type)}</span>
          </div>
          <Progress 
            value={Math.min((competition.my_score / competition.target_value) * 100, 100)} 
            className="h-1.5"
          />
        </div>
      )}
    </div>
  );
};

// Mini Leaderboard Entry
const MiniLeaderEntry = ({ entry, rank }) => {
  const getRankIcon = () => {
    if (rank === 1) return <Crown className="h-4 w-4 text-amber-500" />;
    if (rank === 2) return <Medal className="h-4 w-4 text-slate-400" />;
    if (rank === 3) return <Medal className="h-4 w-4 text-amber-700" />;
    return <span className="w-4 text-center text-xs font-medium">{rank}</span>;
  };

  return (
    <div className="flex items-center gap-2 py-1.5">
      <div className="w-5 flex justify-center">{getRankIcon()}</div>
      <Avatar className="h-6 w-6">
        <AvatarFallback className="text-xs">{entry.entity_name?.charAt(0)}</AvatarFallback>
      </Avatar>
      <span className="flex-1 text-sm truncate">{entry.entity_name}</span>
      <span className="text-sm font-medium">{entry.score?.toLocaleString() || 0}</span>
    </div>
  );
};

export const CompetitionZoneWidget = ({ className = '' }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({
    active_competitions: [],
    my_competitions: [],
    top_performers: [],
    recent_winners: []
  });

  useEffect(() => {
    const fetchData = async () => {
      if (!user?.id) return;
      
      try {
        const response = await axios.get(`${API}/competitions/widget/data?user_id=${user.id}`);
        setData(response.data || {
          active_competitions: [],
          my_competitions: [],
          top_performers: [],
          recent_winners: []
        });
      } catch (error) {
        console.error('Error fetching competition widget data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user?.id]);

  if (loading) {
    return (
      <Card className={className}>
        <CardContent className="p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-4 w-32 bg-slate-200 dark:bg-slate-800 rounded" />
            <div className="h-16 bg-slate-200 dark:bg-slate-800 rounded" />
            <div className="h-16 bg-slate-200 dark:bg-slate-800 rounded" />
          </div>
        </CardContent>
      </Card>
    );
  }

  const hasContent = data.active_competitions?.length > 0 || 
                     data.my_competitions?.length > 0 ||
                     data.top_performers?.length > 0;

  return (
    <Card className={className} data-testid="competition-zone-widget">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Trophy className="h-5 w-5 text-amber-500" />
            Competition Zone
          </CardTitle>
          <Button 
            variant="ghost" 
            size="sm"
            onClick={() => navigate('/competition')}
          >
            View All
            <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {!hasContent ? (
          <div className="text-center py-6">
            <Trophy className="h-10 w-10 mx-auto text-muted-foreground/50 mb-3" />
            <p className="text-sm text-muted-foreground mb-3">
              No active competitions
            </p>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => navigate('/competition')}
            >
              <Zap className="h-4 w-4 mr-2" />
              Explore Competitions
            </Button>
          </div>
        ) : (
          <>
            {/* My Active Competitions */}
            {data.my_competitions?.length > 0 && (
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-2 uppercase tracking-wider">
                  Your Competitions
                </p>
                <div className="space-y-2">
                  {data.my_competitions.slice(0, 2).map(comp => (
                    <MiniCompetitionCard
                      key={comp.id}
                      competition={comp}
                      onClick={() => navigate(`/competition/competitions/${comp.id}`)}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Active Competitions */}
            {data.active_competitions?.length > 0 && data.my_competitions?.length === 0 && (
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-2 uppercase tracking-wider">
                  Active Competitions
                </p>
                <div className="space-y-2">
                  {data.active_competitions.slice(0, 2).map(comp => (
                    <MiniCompetitionCard
                      key={comp.id}
                      competition={comp}
                      onClick={() => navigate(`/competition/competitions/${comp.id}`)}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Top Performers */}
            {data.top_performers?.length > 0 && (
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-2 uppercase tracking-wider">
                  Current Leaders
                </p>
                <div className="space-y-1">
                  {data.top_performers.slice(0, 3).map((entry, idx) => (
                    <MiniLeaderEntry key={entry.entity_id} entry={entry} rank={idx + 1} />
                  ))}
                </div>
              </div>
            )}

            {/* Recent Winner */}
            {data.recent_winners?.length > 0 && (
              <div className="pt-2 border-t">
                <div className="flex items-center gap-2 p-2 bg-amber-50 dark:bg-amber-950/20 rounded-lg">
                  <div className="relative">
                    <Avatar className="h-8 w-8 border-2 border-amber-400">
                      <AvatarFallback className="text-xs bg-amber-100 text-amber-700">
                        {data.recent_winners[0].winner_name?.charAt(0)}
                      </AvatarFallback>
                    </Avatar>
                    <Crown className="absolute -top-1 -right-1 h-4 w-4 text-amber-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{data.recent_winners[0].winner_name}</p>
                    <p className="text-xs text-muted-foreground truncate">
                      Won: {data.recent_winners[0].competition_title}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default CompetitionZoneWidget;
